import { ChangeEvent, ReactNode } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

/**
 * Accepts a raw `register(...)` result OR a `commaInput(...)` result. The only
 * difference is `onChange`: RHF's is an async `ChangeHandler`, while
 * `commaInput` returns a sync handler — so the prop allows either.
 */
type Registration = Omit<UseFormRegisterReturn, 'onChange'> & {
  onChange:
    | UseFormRegisterReturn['onChange']
    | ((event: ChangeEvent<HTMLInputElement>) => void);
};

interface AssetFormFieldProps {
  id: string;
  label: string;
  error?: string;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'numeric';
  /** `register(...)` (or `commaInput(...)`) result, when this field is a plain input. */
  registration?: Registration;
  /** Custom control (select / Controller). When present, replaces the input. */
  children?: ReactNode;
}

/**
 * THT-643 — label + control + error wrapper shared by every text/select field
 * in the asset form. Class names are copied verbatim from the legacy forms so
 * fields render identically. Pass `registration` for a plain input, or
 * `children` for a custom control (e.g. a `<select>` or antd `Controller`).
 */
export default function AssetFormField({
  id,
  label,
  error,
  placeholder,
  type = 'text',
  inputMode,
  registration,
  children,
}: AssetFormFieldProps) {
  return (
    <div className="w-full">
      <label
        htmlFor={id}
        className="block text-[14px] font-medium text-[#858897]"
      >
        {label}
      </label>
      {children ?? (
        <input
          id={id}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          {...registration}
          className={`mt-1 block w-full border px-3 py-2 ${
            error ? 'border-red-500' : 'border-gray-300'
          } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
        />
      )}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}
